(() => {
  if (customElements.get('fw26-slider')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  /* ---------- Video automático: reproducir / pausar ---------- */

  const sendToIframe = (iframe, action) => {
    if (!iframe.contentWindow) return;
    const src = iframe.getAttribute('src') || '';
    let message = null;
    if (src.indexOf('youtube') > -1) {
      message = { event: 'command', func: action === 'play' ? 'playVideo' : 'pauseVideo', args: '' };
    } else if (src.indexOf('vimeo') > -1) {
      message = { method: action };
    }
    if (message) iframe.contentWindow.postMessage(JSON.stringify(message), '*');
  };

  const playBackground = (wrap) => {
    const video = wrap.querySelector('video');
    if (video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.loop = true;
      if (!wrap._fw26Bound) {
        wrap._fw26Bound = true;
        video.addEventListener('playing', () => wrap.classList.add('is-ready'));
      }
      const attempt = video.play();
      if (attempt && attempt.then) {
        attempt.then(() => wrap.classList.add('is-ready')).catch(() => {});
      }
      return;
    }
    const iframe = wrap.querySelector('iframe');
    if (!iframe) return;
    if (!iframe.getAttribute('src')) {
      iframe.addEventListener('load', () => {
        setTimeout(() => wrap.classList.add('is-ready'), 500);
      }, { once: true });
      iframe.setAttribute('src', iframe.dataset.src);
    } else {
      sendToIframe(iframe, 'play');
    }
  };

  const pauseBackground = (wrap) => {
    const video = wrap.querySelector('video');
    if (video) {
      if (!video.paused) video.pause();
      return;
    }
    const iframe = wrap.querySelector('iframe');
    if (iframe && iframe.getAttribute('src')) sendToIframe(iframe, 'pause');
  };

  class Fw26Slider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fw26-track]');
      if (!this.track || this._ready) return;
      this._ready = true;

      this.slides = Array.from(this.track.children);
      this.loop = this.hasAttribute('loop');
      this.page = -1;
      this.perView = 0;
      this.pageCount = 0;

      this.prevButtons = Array.from(this.querySelectorAll('[data-fw26-prev]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-fw26-next]'));
      this.numsEl = this.querySelector('[data-fw26-nums]');
      this.currentEl = this.querySelector('[data-fw26-current]');
      this.totalEl = this.querySelector('[data-fw26-total]');
      this.navEl = this.querySelector('[data-fw26-nav]');

      this.prevButtons.forEach((btn) => btn.addEventListener('click', () => this.goTo(this.page - 1)));
      this.nextButtons.forEach((btn) => btn.addEventListener('click', () => this.goTo(this.page + 1)));

      this.onScroll = () => {
        cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(() => this.sync());
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.measure();

      if (this.hasAttribute('data-fw26-autovideo')) this.initAutoVideo();
    }

    initAutoVideo() {
      this.userPaused = reducedMotion.matches;
      this.inView = false;

      this.viewObserver = new IntersectionObserver((entries) => {
        this.inView = entries[0].isIntersecting;
        this.updateVideos();
      }, { threshold: 0.2 });
      this.viewObserver.observe(this);

      this.addEventListener('fw26:change', () => this.updateVideos());

      this.querySelectorAll('[data-fw26-toggle]').forEach((button) => {
        button.addEventListener('click', () => {
          this.userPaused = !this.userPaused;
          this.updateVideos();
        });
      });

      this.onBreakpoint = () => this.updateVideos();
      mobileQuery.addEventListener('change', this.onBreakpoint);

      this.onVisibility = () => this.updateVideos();
      document.addEventListener('visibilitychange', this.onVisibility);

      this.updateVideos();
    }

    updateVideos() {
      const canPlay = this.inView && !this.userPaused && !document.hidden;
      this.slides.forEach((slide, i) => {
        const isCurrent = Math.floor(i / (this.perView || 1)) === Math.max(this.page, 0);
        slide.querySelectorAll('[data-fw26-bgvideo]').forEach((wrap) => {
          const isShown = wrap.getClientRects().length > 0;
          if (canPlay && isCurrent && isShown) {
            playBackground(wrap);
          } else {
            pauseBackground(wrap);
          }
        });
      });
      this.querySelectorAll('[data-fw26-toggle]').forEach((button) => {
        button.classList.toggle('is-paused', this.userPaused);
        button.setAttribute('aria-pressed', this.userPaused ? 'true' : 'false');
        button.setAttribute('aria-label', this.userPaused ? 'Reproducir video' : 'Pausar video');
      });
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.viewObserver) this.viewObserver.disconnect();
      if (this.onBreakpoint) mobileQuery.removeEventListener('change', this.onBreakpoint);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      this._ready = false;
    }

    measure() {
      const first = this.slides[0];
      if (!first) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const slideWidth = first.getBoundingClientRect().width;
      if (!slideWidth) return;

      const perView = Math.max(1, Math.round((this.track.clientWidth + gap) / (slideWidth + gap)));
      const pageCount = Math.max(1, Math.ceil(this.slides.length / perView));

      if (perView !== this.perView || pageCount !== this.pageCount) {
        this.perView = perView;
        this.pageCount = pageCount;
        this.buildPagination();
      }
      this.sync(true);
    }

    buildPagination() {
      if (this.navEl) this.navEl.hidden = this.pageCount < 2;
      if (this.totalEl) this.totalEl.textContent = this.pageCount;
      if (!this.numsEl) return;

      this.numsEl.innerHTML = '';
      for (let i = 0; i < this.pageCount; i++) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fw26-page';
        button.textContent = String(i + 1).padStart(2, '0');
        button.setAttribute('aria-label', `Ir a ${i + 1} de ${this.pageCount}`);
        button.addEventListener('click', () => this.goTo(i));
        this.numsEl.appendChild(button);
      }
    }

    offsetOf(slide) {
      return slide.offsetLeft - this.slides[0].offsetLeft;
    }

    currentPage() {
      const left = this.track.scrollLeft;
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 1) return 0;
      if (left >= max - 2) return this.pageCount - 1;

      let index = 0;
      let best = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(this.offsetOf(slide) - left);
        if (distance < best) {
          best = distance;
          index = i;
        }
      });
      return Math.min(this.pageCount - 1, Math.floor(index / this.perView));
    }

    sync(force = false) {
      if (!this.pageCount) return;
      const page = this.currentPage();
      const changed = page !== this.page;
      if (!changed && !force) return;
      this.page = page;

      if (this.numsEl) {
        Array.from(this.numsEl.children).forEach((button, i) => {
          button.setAttribute('aria-current', i === page ? 'true' : 'false');
        });
      }
      if (this.currentEl) this.currentEl.textContent = page + 1;

      if (!this.loop) {
        this.prevButtons.forEach((btn) => { btn.disabled = page === 0; });
        this.nextButtons.forEach((btn) => { btn.disabled = page >= this.pageCount - 1; });
      }

      if (changed) {
        this.dispatchEvent(new CustomEvent('fw26:change', { detail: { page }, bubbles: true }));
      }
    }

    goTo(page) {
      if (!this.pageCount) return;
      if (this.loop) {
        page = ((page % this.pageCount) + this.pageCount) % this.pageCount;
      } else {
        page = Math.max(0, Math.min(this.pageCount - 1, page));
      }
      const target = this.slides[Math.min(page * this.perView, this.slides.length - 1)];
      this.track.scrollTo({
        left: this.offsetOf(target),
        behavior: reducedMotion.matches ? 'auto' : 'smooth'
      });
    }

    goToSlide(slide) {
      const index = this.slides.indexOf(slide);
      if (index > -1) this.goTo(Math.floor(index / (this.perView || 1)));
    }
  }

  customElements.define('fw26-slider', Fw26Slider);

  /* ---------- Reproductor de video (El libro) ---------- */

  const openPlayer = (slide) => {
    if (!slide || slide.classList.contains('is-playing')) return;
    const template = slide.querySelector('template[data-fw26-video]');
    const holder = slide.querySelector('[data-fw26-player]');
    if (!template || !holder) return;

    holder.innerHTML = '';
    const content = template.content.cloneNode(true);
    const unused = mobileQuery.matches ? '.fw26-only-d' : '.fw26-only-m';
    content.querySelectorAll(unused).forEach((el) => el.remove());
    holder.appendChild(content);
    holder.hidden = false;
    slide.classList.add('is-playing');

    const video = holder.querySelector('video');
    if (video) {
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    }
    const close = slide.querySelector('[data-fw26-close]');
    if (close) close.focus({ preventScroll: true });
  };

  const closePlayer = (slide, restoreFocus) => {
    if (!slide || !slide.classList.contains('is-playing')) return;
    const holder = slide.querySelector('[data-fw26-player]');
    if (holder) {
      holder.innerHTML = '';
      holder.hidden = true;
    }
    slide.classList.remove('is-playing');
    if (restoreFocus) {
      const play = slide.querySelector('[data-fw26-play]');
      if (play) play.focus({ preventScroll: true });
    }
  };

  document.addEventListener('click', (event) => {
    const play = event.target.closest('[data-fw26-play]');
    if (play) {
      openPlayer(play.closest('[data-fw26-vslide]'));
      return;
    }
    const close = event.target.closest('[data-fw26-close]');
    if (close) closePlayer(close.closest('[data-fw26-vslide]'), true);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const slide = event.target.closest && event.target.closest('[data-fw26-vslide].is-playing');
    if (slide) closePlayer(slide, true);
  });

  document.addEventListener('fw26:change', (event) => {
    event.target.querySelectorAll('[data-fw26-vslide].is-playing').forEach((slide) => closePlayer(slide, false));
  });

  /* ---------- Editor de temas: llevar el carrusel al bloque seleccionado ---------- */

  document.addEventListener('shopify:block:select', (event) => {
    const slider = event.target.closest('fw26-slider');
    if (slider && typeof slider.goToSlide === 'function') slider.goToSlide(event.target);
  });
})();
